
export const formatValue = (val, columnId, rowIndex, columns) => {
  // Handle null, undefined, NaN, and empty strings
  if (val === "" || val == null || val === undefined || 
      (typeof val === 'number' && (isNaN(val) || !isFinite(val)))) {
    return "";
  }
  
  if (columnId === columns[0]?.id) {
    return String(val);
  }
  
  if (typeof val === "number") {
    if (val < 0) {
      return `(${Math.abs(val).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })})`;
    }
    return val.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
  
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (trimmed === "") return "";
    
    if (trimmed.startsWith("(") || trimmed.startsWith("$")) {
      return trimmed;
    }
    
    // Try to parse as number
    const num = parseFloat(trimmed.replace(/[^0-9.-]/g, ''));
    if (!isNaN(num) && isFinite(num)) {
      if (num < 0) {
        return `(${Math.abs(num).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })})`;
      }
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    
    return val;
  }
  
  // For any other type, convert to string
  return String(val);
};

export const shouldBeBold = (value, rowIndex, columnIndex) => {
  if (!value) return false;
  
  const valueStr = String(value).toLowerCase();
  const boldItems = [
    "cash flow statement", "operating activities", "financing activities",
    "investing activities", "income statement", "balance sheet",
    "net sales", "cost of sales", "gross profit", "operating income",
    "net income", "total assets", "total liabilities",
    "cash flows from operating activities", "net cash generated from operating activities",
    "profit before tax"
  ];
  
  return boldItems.some(item => valueStr.includes(item.toLowerCase()));
};

export const formatDataForTable = (jsonData, companyName, statementType) => {
  if (!jsonData || !Array.isArray(jsonData) || jsonData.length === 0) {
    console.warn("No data provided to formatDataForTable");
    return [];
  }
  
  const allColumns = new Set();
  if (jsonData.length > 0) {
    Object.keys(jsonData[0]).forEach(key => allColumns.add(key));
  }
  
  const columns = Array.from(allColumns);
  
  // Create company row
  const companyRow = {};
  columns.forEach((header, index) => {
    if (index === 0) {
      companyRow[header] = companyName || "";
    } else {
      companyRow[header] = "";
    }
  });
  
  // Check if data already contains statement name
  const firstRowValue = String(jsonData[0]?.[columns[0]] || '').toLowerCase();
  const statementTypeLower = String(statementType).toLowerCase();
  
  let formattedData;
  
  if (firstRowValue.includes('statement') || firstRowValue.includes(statementTypeLower)) {
    // Data already has statement name
    const cleanedData = jsonData.map((row, rowIndex) => {
      const formattedRow = {};
      
      columns.forEach((header, colIndex) => {
        let value = row[header];
        
        // Handle null/undefined/NaN
        if (value === null || value === undefined || 
            (typeof value === 'number' && (isNaN(value) || !isFinite(value)))) {
          value = "";
        }
        
        let stringValue = String(value).trim();
        
        // Format years in header row
        if (rowIndex === 2 && colIndex > 0 && stringValue) {
          const yearMatch = stringValue.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            formattedRow[header] = `FY ${yearMatch[0]}`;
            return;
          }
        }
        
        // Handle numeric values
        if (rowIndex > 2 && colIndex > 0 && stringValue !== '') {
          const cleaned = stringValue.replace(/[$,%\s()]/g, '');
          
          if (cleaned !== '' && !isNaN(cleaned) && isFinite(cleaned)) {
            const numValue = parseFloat(cleaned);
            
            if (stringValue.includes('(') && stringValue.includes(')')) {
              formattedRow[header] = -Math.abs(numValue);
              return;
            } else {
              formattedRow[header] = numValue;
              return;
            }
          }
        }
        
        formattedRow[header] = value;
      });
      
      return formattedRow;
    });
    
    formattedData = [companyRow, ...cleanedData];
    
  } else {
    // Create standard structure
    const statementRow = {};
    columns.forEach((header, index) => {
      if (index === 0) {
        statementRow[header] = (statementType || "").toUpperCase();
      } else {
        statementRow[header] = "";
      }
    });
    
    const emptyRow = {};
    columns.forEach((header) => {
      emptyRow[header] = "";
    });
    
    const cleanedData = jsonData.map((row, rowIndex) => {
      const formattedRow = {};
      
      columns.forEach((header, colIndex) => {
        let value = row[header];
        
        // Handle null/undefined/NaN
        if (value === null || value === undefined || 
            (typeof value === 'number' && (isNaN(value) || !isFinite(value)))) {
          value = "";
        }
        
        let stringValue = String(value).trim();
        
        // For first row, format years
        if (rowIndex === 0 && colIndex > 0 && stringValue) {
          const yearMatch = stringValue.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            formattedRow[header] = `FY ${yearMatch[0]}`;
            return;
          }
        }
        
        // Handle numeric values
        if (rowIndex > 0 && colIndex > 0 && stringValue !== '') {
          const cleaned = stringValue.replace(/[$,%\s()]/g, '');
          
          if (cleaned !== '' && !isNaN(cleaned) && isFinite(cleaned)) {
            const numValue = parseFloat(cleaned);
            
            if (stringValue.includes('(') && stringValue.includes(')')) {
              formattedRow[header] = -Math.abs(numValue);
              return;
            } else {
              formattedRow[header] = numValue;
              return;
            }
          }
        }
        
        formattedRow[header] = value;
      });
      
      return formattedRow;
    });
    
    formattedData = [
      companyRow,
      statementRow,
      emptyRow,
      ...cleanedData
    ];
  }
  
  console.log(`Formatted ${formattedData.length} rows for table`);
  return formattedData;
};

// Helper function to clean Excel data
export const cleanExcelData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  
  return data.map(row => {
    const cleanedRow = {};
    Object.keys(row).forEach(key => {
      let value = row[key];
      
      // Handle NaN and Infinity
      if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
        value = null;
      }
      
      // Convert to string if it's a Date
      if (value instanceof Date) {
        value = value.toISOString().split('T')[0];
      }
      
      cleanedRow[key] = value;
    });
    return cleanedRow;
  });
};
